import { Component, OnInit } from "@angular/core";
import { BsModalRef } from "ngx-bootstrap/modal";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

/** Same behavior as wa-engage-v2 `message/media-preview-modal` — preview MMS attachments. */
@Component({
  selector: "app-media-preview-modal",
  templateUrl: "./media-preview-modal.component.html",
  styleUrls: ["./media-preview-modal.component.scss"],
})
export class MediaPreviewModalComponent implements OnInit {
  mediaUrl = "";
  mediaContentType = "";
  mediaFileName = "attachment";

  isLoading = true;
  loadError = false;
  safeMediaUrl: SafeResourceUrl | null = null;
  private blobUrl: string | null = null;

  constructor(
    public bsModalRef: BsModalRef,
    private readonly sanitizer: DomSanitizer
  ) {
    const initialState = (this.bsModalRef as any).initialState || {};
    if (initialState.mediaUrl) {
      this.mediaUrl = initialState.mediaUrl;
    }
    if (initialState.mediaContentType) {
      this.mediaContentType = initialState.mediaContentType;
    }
    if (initialState.mediaFileName) {
      this.mediaFileName = initialState.mediaFileName;
    }
  }

  ngOnInit(): void {
    const initialState = (this.bsModalRef as any).initialState || {};
    if (!this.mediaUrl && initialState.mediaUrl) {
      this.mediaUrl = initialState.mediaUrl;
    }
    if (!this.mediaContentType && initialState.mediaContentType) {
      this.mediaContentType = initialState.mediaContentType;
    }
    if (!this.mediaFileName && initialState.mediaFileName) {
      this.mediaFileName = initialState.mediaFileName;
    }
    if (!this.mediaUrl) {
      this.isLoading = false;
      this.loadError = true;
      return;
    }
    if (this.isImage) {
      const img = new Image();
      img.onload = () => this.createImageHtmlViewer();
      img.onerror = () => this.createImageHtmlViewer();
      img.src = this.mediaUrl;
    } else if (this.isPdf) {
      try {
        this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.mediaUrl);
      } catch {
        this.safeMediaUrl = null;
      }
      setTimeout(() => {
        this.isLoading = false;
      }, 500);
    } else {
      this.isLoading = false;
    }
  }

  private createImageHtmlViewer(): void {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body { margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center;
            min-height: 100vh; background: #f5f5f5; }
          img { max-width: 100%; max-height: 100vh; object-fit: contain;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2); border-radius: 8px; }
        </style>
      </head>
      <body>
        <img src="${this.mediaUrl}" alt="${this.mediaFileName}" />
      </body>
      </html>
    `;
    const blob = new Blob([htmlContent], { type: "text/html" });
    this.blobUrl = URL.createObjectURL(blob);
    this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
    this.isLoading = false;
    this.loadError = false;
  }

  get isImage(): boolean {
    return this.mediaContentType?.startsWith("image/") ?? false;
  }

  get isPdf(): boolean {
    return this.mediaContentType === "application/pdf";
  }

  downloadFile(): void {
    if (!this.mediaUrl) {
      return;
    }
    fetch(this.mediaUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = this.mediaFileName || "attachment";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        window.open(this.mediaUrl, "_blank");
      });
  }

  close(): void {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
    this.bsModalRef.hide();
  }
}
